(() => {
  // Below this width the card swiper is a touch peek carousel; above it the card floats and animates.
  const CARD_LAYOUT_BREAKPOINT = 990;
  const CARD_GAP = 16;
  // Mobile peek needs `ceil(max slidesPerView) + 1` cards before Swiper's loop snaps correctly.
  const MIN_MOBILE_LOOP_SLIDES = 3;

  // State lives ON the section element, not in this closure: the theme editor re-runs the script on
  // every section reload, so a later execution must be able to find and tear down what an earlier one
  // built on the same element — otherwise two live Swipers end up driving one track.
  const SECTION_STATE = "__themeShowcaseSlideshowState";

  // Hold autoplay until window `load` — advancing during page load churns Speed Index / main thread.
  let pageLoaded = document.readyState === "complete";
  const onPageLoad = new Set();
  if (!pageLoaded) {
    window.addEventListener(
      "load",
      () => {
        pageLoaded = true;
        onPageLoad.forEach((cb) => cb());
      },
      { once: true }
    );
  }

  // Swiper's `loopFix` re-orders the slides and re-emits `slideChange` with the SAME realIndex (it
  // runs inside every slideNext/slidePrev/drag in loop mode), so positional indices are not stable
  // across a slide change either. Gate every handler on a real index change and address slides by
  // element, never by `previousIndex`.
  const onRealIndexChange = (swiper, callback) => {
    let last = swiper.realIndex ?? swiper.activeIndex ?? 0;
    swiper.on("slideChange", () => {
      const realIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;
      if (realIndex === last) return;
      const previous = last;
      last = realIndex;
      callback(realIndex, previous);
    });
  };

  const getSlideVideos = (slide) =>
    slide
      ? Array.from(slide.querySelectorAll(".showcase-slideshow__video"))
      : [];

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const activeSlide = swiper.slides[swiper.activeIndex];

    swiper.slides.forEach((slide) => {
      getSlideVideos(slide).forEach((video) => {
        // A slide can hold both the desktop and the mobile video — only the displayed one plays.
        if (slide === activeSlide && video.offsetParent !== null) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    swiper.slides.forEach((slide) => {
      getSlideVideos(slide).forEach((video) => video.pause());
    });
  };

  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // No `preventInteractionOnTransition`: with slide+loop, loopFix can strand Swiper's `animating`
  // flag and freeze nav; leaving it off lets the next slideTo self-heal the stuck flag.
  const getBaseParams = () => ({
    speed: 1000,
    allowTouchMove: true,
    watchSlidesProgress: true,
  });

  // creative: slides stay stacked/still so the JS clip-path wipe is the only motion; the 0.99 opacity
  // is an imperceptible transition that lets Swiper detect transition end under virtualTranslate.
  const getMediaEffectParams = (box) => {
    const anim = box.dataset.animation || "slide";
    if (anim === "fade") {
      return { effect: "fade", fadeEffect: { crossFade: true } };
    }
    if (anim === "creative") {
      // 700ms matches the clip-path wipe; it's also the speed handed to the linked card swiper.
      return {
        effect: "creative",
        speed: 700,
        creativeEffect: {
          prev: {
            translate: [0, 0, 0],
            rotate: [0, 0, 0],
            scale: 1,
            opacity: 0.99,
          },
          next: {
            translate: [0, 0, 0],
            rotate: [0, 0, 0],
            scale: 1,
            opacity: 0.99,
          },
        },
      };
    }
    return { effect: "slide" };
  };

  // With a fractional slidesPerView the track stops once its end meets the container, so the last card
  // never reaches the start edge and gets no snap of its own — it can't be swiped to and can't line up
  // with the media slide it belongs to. Padding the track by the peek width gives every card a snap.
  // Function form: Swiper re-reads it on each update, so it follows resizes and breakpoint changes.
  const getPeekOffsetAfter = function () {
    const spv = this.params.slidesPerView;
    if (typeof spv !== "number" || spv <= 1 || !this.size) return 0;
    const gap = this.params.spaceBetween || 0;
    const slideSize = (this.size - (spv - 1) * gap) / spv;
    return Math.max(0, this.size - slideSize);
  };

  // A real loop needs a horizontally moving track: loopFix re-orders the slides, which the stacked
  // fade/creative effects can't absorb mid-transition. Mobile always renders the peek carousel
  // (effect `slide`), so there it only depends on having enough cards.
  const canLoopSlides = (box, isMobile, slideCount) => {
    if (isMobile) return slideCount >= MIN_MOBILE_LOOP_SLIDES;
    return (box.dataset.animation || "slide") === "slide";
  };

  // Mobile: horizontal peek carousel. Desktop: matches the transition — creative (vertical),
  // slide (horizontal), fade (cross-fade). The gap keeps cards from touching mid-transition.
  const getCardParams = (box, isMobile, willLoop) => {
    const anim = box.dataset.animation || "slide";
    const params = getBaseParams();
    params.spaceBetween = CARD_GAP;
    params.allowTouchMove = isMobile;

    if (isMobile) {
      params.slidesPerView = 1.05;
      // A looping track cycles, so every card already has a snap — the peek padding would only skew
      // the snap grid away from the media index.
      if (!willLoop) params.slidesOffsetAfter = getPeekOffsetAfter;
      params.breakpoints = {
        576: {
          slidesPerView: 1.25,
        },
        750: {
          slidesPerView: 1.45,
        },
      };
    } else if (anim === "creative") {
      // creative: slides stack and the active one translates vertically (next up, prev down). The SCSS
      // stretches every slide to the tallest card's height so a shorter neighbour still clears the box
      // (no autoHeight — the translate is a % of each slide's own height).
      params.effect = "creative";
      params.speed = 800;
      // 114% (>100%) keeps a constant gap between outgoing/incoming so they read as separate.
      params.creativeEffect = {
        prev: { translate: [0, "-114%", 0] },
        next: { translate: [0, "114%", 0] },
      };
    } else if (anim === "fade") {
      params.effect = "fade";
      params.fadeEffect = { crossFade: true };
    }

    return params;
  };

  // Loopable track → real loop; otherwise rewind (a stacked effect can't take the re-order, and too
  // few slides make Swiper's loop mis-snap).
  const applyLoopParams = (params, box, willLoop) => {
    if (box.dataset.loop !== "true") return;
    if (willLoop) {
      params.loop = true;
      params.loopPreventsSliding = false;
    } else {
      params.rewind = true;
    }
  };

  const initMediaSwiper = (box) => {
    const el = box.querySelector(".showcase-slideshow__media-swiper");
    if (!el) return null;
    const slideCount = el.querySelectorAll(".swiper-slide").length;
    if (slideCount < 2) return null;

    const params = getBaseParams();
    Object.assign(params, getMediaEffectParams(box));
    applyLoopParams(params, box, canLoopSlides(box, false, slideCount));

    if (box.dataset.autoplay === "true" && !prefersReducedMotion()) {
      const delay = Number(box.dataset.autoplayDuration || 5) * 1000;
      params.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
    }

    const swiper = new Swiper(el, params);
    onRealIndexChange(swiper, () => playVideo(swiper));

    return swiper;
  };

  const initCardSwiper = (box, isMobile) => {
    const el = box.querySelector(".showcase-slideshow__cards-swiper");
    if (!el) return null;
    const slideCount = el.querySelectorAll(".swiper-slide").length;
    if (slideCount < 2) return null;

    const willLoop = canLoopSlides(box, isMobile, slideCount);
    const params = getCardParams(box, isMobile, willLoop);
    applyLoopParams(params, box, willLoop);

    return new Swiper(el, params);
  };

  // Step, don't jump. `slideToLoop` picks its own side, and with few slides the loop rotation lands the
  // target left of the active one every other time — the strip then visibly runs backwards while the
  // media goes forward. slideNext/slidePrev always animate one slide the way the user is heading and
  // keep the re-shuffle off-screen. Multi-slide jumps (pagination) have no direction to preserve.
  const slideToIndex = (swiper, index) => {
    if (!swiper || swiper.destroyed) return;
    const current = swiper.realIndex;
    if (current === index) return;
    if (!swiper.params.loop) {
      swiper.slideTo(index);
      return;
    }

    const total = swiper.slides.length;
    let delta = (index - current) % total;
    if (delta > total / 2) delta -= total;
    if (delta < -total / 2) delta += total;

    if (delta === 1) {
      swiper.slideNext();
    } else if (delta === -1) {
      swiper.slidePrev();
    } else {
      swiper.slideToLoop(index);
    }
  };

  // Mutual control by index, not Swiper's `controller`: the controller mirrors the master's TRANSLATE,
  // and in loop mode Swiper pins the translate and rotates the slides instead, so the linked swiper
  // stays put and drifts onto the wrong card. Returns the card-side binder — the media handler is
  // registered once, the card one is re-bound after a breakpoint rebuild.
  const linkSwipers = (mediaSwiper, getCardSwiper) => {
    if (!mediaSwiper) return () => {};
    let syncing = false;
    const sync = (from, getTo) => () => {
      if (syncing) return;
      syncing = true;
      slideToIndex(getTo(), from.realIndex);
      syncing = false;
    };

    onRealIndexChange(mediaSwiper, sync(mediaSwiper, getCardSwiper));

    return () => {
      const cardSwiper = getCardSwiper();
      if (!cardSwiper) return;
      onRealIndexChange(
        cardSwiper,
        sync(cardSwiper, () => mediaSwiper)
      );
    };
  };

  // Manual nav doesn't restart the countdown, so restart it. Guarded by `running` so we don't
  // revive an autoplay the lifecycle paused (off-screen / keyboard focus).
  const resetAutoplay = (mediaSwiper) => {
    const autoplay = mediaSwiper && mediaSwiper.autoplay;
    if (!autoplay || !autoplay.running) return;
    autoplay.stop();
    autoplay.start();
  };

  const initPagination = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const paginationEl = box.querySelector(".showcase-slideshow__pagination");
    if (!paginationEl) return;

    const bullets = Array.from(
      paginationEl.querySelectorAll(".showcase-slideshow__bullet")
    );
    if (bullets.length === 0) return;

    const getActiveRealIndex = () =>
      mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    const hasAutoplay = box.dataset.autoplay === "true";

    const setActiveBullet = (realIndex) => {
      bullets.forEach((bullet, idx) => {
        const isActive = idx === realIndex;
        bullet.classList.toggle("is-active", isActive);
        bullet.setAttribute("aria-selected", isActive ? "true" : "false");
        // Autoplay bar is a pure CSS animation; the `--bullet-progress` dance is no-autoplay only.
        if (hasAutoplay) return;
        if (isActive) {
          bullet.style.removeProperty("--bullet-progress");
        } else {
          bullet.style.setProperty("--bullet-progress", "0");
        }
      });
    };

    bullets.forEach((bullet) => {
      bullet.addEventListener("click", () => {
        const idx = Number(bullet.dataset.slideIndex);
        if (Number.isNaN(idx)) return;
        if (mediaSwiper.params.loop) {
          mediaSwiper.slideToLoop(idx);
        } else {
          mediaSwiper.slideTo(idx);
        }
        resetAutoplay(mediaSwiper);
      });
    });

    // Track nav direction for the progress-bar fill (CSS `.is-nav-prev`); wrap handled explicitly.
    onRealIndexChange(mediaSwiper, (realIndex, previous) => {
      const total = bullets.length;
      let isPrev;
      if (previous === total - 1 && realIndex === 0) {
        isPrev = false;
      } else if (previous === 0 && realIndex === total - 1) {
        isPrev = true;
      } else {
        isPrev = realIndex < previous;
      }
      box.classList.toggle("is-nav-prev", isPrev);
      setActiveBullet(realIndex);
    });

    setActiveBullet(getActiveRealIndex());
  };

  // Drive autoplay from viewport visibility + keyboard focus. stop()/start() (not pause()/resume())
  // since pause() is cleared by the next slideTo, reviving a paused autoplay after manual nav.
  const initLifecycle = (box, mediaSwiper, cleanups) => {
    if (!mediaSwiper) return;
    const hasAutoplay = box.dataset.autoplay === "true";

    let isVisible = false;
    let isFocusWithin = false;

    const sync = () => {
      if (mediaSwiper.destroyed) return;
      if (isVisible) playVideo(mediaSwiper);
      else stopVideo(mediaSwiper);

      if (!hasAutoplay || !mediaSwiper.autoplay) return;
      const shouldRun = isVisible && !isFocusWithin && pageLoaded;
      if (shouldRun) {
        mediaSwiper.autoplay.start();
      } else {
        mediaSwiper.autoplay.stop();
      }
      // The CSS progress-bar countdown runs only while autoplay actually runs.
      box.classList.toggle("is-autoplay-running", mediaSwiper.autoplay.running);
    };

    // Swiper auto-starts autoplay on init; stop it now — the observer + load callback re-enable it.
    sync();
    onPageLoad.add(sync);
    cleanups.push(() => onPageLoad.delete(sync));

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting;
        sync();
      });
    });
    observer.observe(box);
    cleanups.push(() => observer.disconnect());

    // A resize desyncs Swiper's timer from the running CSS bar; once it settles, restart both from zero.
    let resizeTimer = null;
    cleanups.push(() => window.clearTimeout(resizeTimer));
    mediaSwiper.on("resize", () => {
      if (!hasAutoplay || !mediaSwiper.autoplay) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (mediaSwiper.destroyed || !mediaSwiper.autoplay.running) return;
        mediaSwiper.autoplay.stop();
        mediaSwiper.autoplay.start();
        box.classList.remove("is-autoplay-running");
        void box.offsetWidth;
        box.classList.add("is-autoplay-running");
      }, 150);
    });

    // `is-counting` mirrors Swiper's real countdown (which only starts after the slide transition
    // ends) so the CSS fill doesn't finish ahead of the actual slide change.
    if (hasAutoplay) {
      onRealIndexChange(mediaSwiper, () => {
        box.classList.remove("is-counting");
      });
      mediaSwiper.on("autoplayTimeLeft", (_s, _time, progress) => {
        // During the transition Swiper ticks with `paused: true` at the full delay — skip those.
        if (mediaSwiper.autoplay.paused) return;
        if (progress > 0.9 && !box.classList.contains("is-counting")) {
          box.classList.add("is-counting");
        }
      });
    }

    box.addEventListener("focusin", (event) => {
      const target = event.target;
      if (!target || typeof target.matches !== "function") return;
      if (!target.matches(":focus-visible")) return;
      isFocusWithin = true;
      sync();
    });
    box.addEventListener("focusout", (event) => {
      if (!isFocusWithin) return;
      if (box.contains(event.relatedTarget)) return;
      isFocusWithin = false;
      sync();
    });
  };

  const initCursorArrows = (box, mediaSwiper, cleanups) => {
    if (!mediaSwiper) return;
    if (box.dataset.cursorArrows !== "true") return;
    if (typeof window.initCursorArrowZone !== "function") return;

    const zone = box.querySelector(".showcase-slideshow__arrow-zone");
    if (!zone) return;

    // Loop / rewind always wrap, so only a finite slider can reach an inert end.
    const finite = !mediaSwiper.params.loop && !mediaSwiper.params.rewind;

    const nav = window.initCursorArrowZone(zone, {
      onNavigate: (direction) => {
        if (direction === "next") {
          mediaSwiper.slideNext();
        } else {
          mediaSwiper.slidePrev();
        }
        resetAutoplay(mediaSwiper);
      },
      isEndReached: (direction) => {
        if (!finite) return false;
        return direction === "prev"
          ? mediaSwiper.isBeginning
          : mediaSwiper.isEnd;
      },
    });

    cleanups.push(nav.destroy);
    if (finite) mediaSwiper.on("slideChange", nav.sync);
  };

  // Creative: wipe the incoming media in via clip-path (up on next, down on prev). The previous slide
  // stays visible underneath as the underlay; the clip lives on `__media` so it doesn't fight Swiper.
  const initCreativeMedia = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    if (box.dataset.animation !== "creative") return;

    const HIDDEN_NEXT = "inset(100% 0 0 0)"; // clipped from the bottom → reveals upward
    const HIDDEN_PREV = "inset(0 0 100% 0)"; // clipped from the top → reveals downward
    const SHOWN = "inset(0 0 0 0)";

    const getMedia = (slide) =>
      slide ? slide.querySelector(".showcase-slideshow__media") : null;

    const setInstant = (media, value) => {
      if (!media) return;
      media.style.transition = "none";
      media.style.clipPath = value;
    };

    if (prefersReducedMotion()) {
      mediaSwiper.slides.forEach((slide) => setInstant(getMedia(slide), SHOWN));
      return;
    }

    mediaSwiper.slides.forEach((slide, i) => {
      setInstant(
        getMedia(slide),
        i === mediaSwiper.activeIndex ? SHOWN : HIDDEN_NEXT
      );
    });

    onRealIndexChange(mediaSwiper, (realIndex, previous) => {
      const { slides, activeIndex } = mediaSwiper;
      const total = slides.length;
      const activeSlide = slides[activeIndex];
      // `data-swiper-slide-index` exists in loop mode only; without it the position IS the real index.
      const previousSlide =
        slides.find(
          (slide) =>
            Number(slide.getAttribute("data-swiper-slide-index") ?? -1) ===
            previous
        ) || slides[previous];

      // Direction, with the rewind wrap handled explicitly.
      let dir;
      if (previous === total - 1 && realIndex === 0) dir = "next";
      else if (previous === 0 && realIndex === total - 1) dir = "prev";
      else dir = realIndex > previous ? "next" : "prev";

      const HIDDEN = dir === "prev" ? HIDDEN_PREV : HIDDEN_NEXT;

      slides.forEach((slide) => {
        const media = getMedia(slide);
        if (!media) return;
        if (slide === activeSlide) {
          // Force a reflow so the wipe always starts from the fully-clipped state.
          media.style.transition = "none";
          media.style.clipPath = HIDDEN;
          void media.offsetWidth;
          media.style.transition =
            "clip-path 0.7s cubic-bezier(0.45, 0.75, 0.1, 0.9)";
          media.style.clipPath = SHOWN;
        } else if (slide === previousSlide) {
          setInstant(media, SHOWN);
        } else {
          setInstant(media, HIDDEN);
        }
      });
    });
  };

  // Two-finger horizontal trackpad swipe → prev/next (vertical keeps scrolling). preventDefault blocks
  // browser back/forward.
  // The lock outlasts the transition AND keeps being pushed out while the gesture (its momentum tail
  // included) still delivers events, so one swipe can only ever advance one slide.
  const SLIDE_COOLDOWN = 1000;
  const GESTURE_TAIL = 250;
  const initWheelNav = (box, mediaSwiper) => {
    if (!mediaSwiper) return;

    let unlockAt = 0;
    const extendLock = (ms) => {
      unlockAt = Math.max(unlockAt, Date.now() + ms);
    };

    box.addEventListener(
      "wheel",
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();

        if (Math.abs(event.deltaX) < 8) return;
        if (Date.now() < unlockAt) {
          extendLock(GESTURE_TAIL);
          return;
        }
        extendLock(SLIDE_COOLDOWN);

        if (event.deltaX > 0) {
          mediaSwiper.slideNext();
        } else {
          mediaSwiper.slidePrev();
        }
        resetAutoplay(mediaSwiper);
      },
      { passive: false }
    );
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("showcase-slideshow-section"))
      return;

    const box = section.querySelector(".showcase-slideshow");
    if (!box) return;

    destroySection(section);

    const cleanups = [];
    section[SECTION_STATE] = { observer: null, cleanups };

    const mediaSwiper = initMediaSwiper(box);
    cleanups.push(() => {
      if (mediaSwiper && !mediaSwiper.destroyed)
        mediaSwiper.destroy(true, true);
    });

    const mq = window.matchMedia(
      `(max-width: ${CARD_LAYOUT_BREAKPOINT - 1}px)`
    );
    let isMobile = mq.matches;
    let cardSwiper = initCardSwiper(box, isMobile);
    cleanups.push(() => {
      if (cardSwiper && !cardSwiper.destroyed) cardSwiper.destroy(true, true);
    });

    const bindCardSync = linkSwipers(mediaSwiper, () => cardSwiper);
    bindCardSync();
    initCreativeMedia(box, mediaSwiper);
    initPagination(box, mediaSwiper);
    initCursorArrows(box, mediaSwiper, cleanups);
    initWheelNav(box, mediaSwiper);
    initLifecycle(box, mediaSwiper, cleanups);

    // Card axis/effect is fixed at construction, so crossing the breakpoint needs a rebuild + re-sync.
    const onBreakpointChange = (event) => {
      if (!cardSwiper) return;
      if (event.matches === isMobile) return;
      isMobile = event.matches;

      const idx = mediaSwiper ? mediaSwiper.realIndex ?? 0 : 0;
      cardSwiper.destroy(true, true);
      cardSwiper = initCardSwiper(box, isMobile);
      if (cardSwiper) {
        if (cardSwiper.params.loop) {
          cardSwiper.slideToLoop(idx, 0);
        } else {
          cardSwiper.slideTo(idx, 0);
        }
      }
      bindCardSync();
    };
    mq.addEventListener("change", onBreakpointChange);
    cleanups.push(() => mq.removeEventListener("change", onBreakpointChange));
  };

  const destroySection = (section) => {
    if (!section) return;
    const state = section[SECTION_STATE];
    if (!state) return;
    section[SECTION_STATE] = null;
    if (state.observer) state.observer.disconnect();
    state.cleanups.forEach((cleanup) => cleanup());
  };

  // Defer setup until the section nears the viewport (400px ahead) to keep init cost off page load.
  const lazyInitSection = (section) => {
    if (!section || !section.classList.contains("showcase-slideshow-section")) {
      return;
    }
    if (typeof IntersectionObserver !== "function") {
      initSection(section);
      return;
    }
    destroySection(section);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initSection(section);
      },
      { rootMargin: "400px 0px" }
    );
    section[SECTION_STATE] = { observer, cleanups: [] };
    observer.observe(section);
  };

  lazyInitSection(document.currentScript.parentElement);

  // The editor re-executes this script on every section reload — bind the document listeners once,
  // otherwise each reload adds another one and they all init the same section.
  if (!window.__themeShowcaseSlideshowEvents) {
    window.__themeShowcaseSlideshowEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      lazyInitSection(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      destroySection(event.target);
    });
  }
})();
